/**
 * The emergency activation's confirmation (docs/redundancy.md, *Emergency
 * activation*; wave 7b-tail C): forcing this node's standby on air while its
 * active node and every witness are unreachable. The split-brain risk is the
 * first thing it says; the operator types the production's name and a reason
 * (kept in the audit and the tenant's `forced-promotion` alert). The node
 * decides again with fresh evidence and refuses when anything says the active
 * is alive — its refusal comes back as `error`.
 */
import { TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';

export type ForcePromoteDialogProps = {
  readonly open: boolean;
  /** What the operator must type: the production's name (its rundown's or playlist's), else its id. */
  readonly confirmation: string;
  /** The node the claim names active now (unreachable). */
  readonly activeNode: string | null;
  /** This node: whose standby goes on air. */
  readonly standbyNode: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onConfirm: (input: { readonly confirm: string; readonly reason: string }) => void;
  readonly onOpenChange: (open: boolean) => void;
};

function Body({ confirmation, activeNode, standbyNode, pending, error, onConfirm, onOpenChange }: Omit<ForcePromoteDialogProps, 'open'>) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const ready = typed.trim() === confirmation.trim() && reason.trim() !== '';
  return (
    <>
      <DialogHeader>
        <DialogTitle>Force the standby on air?</DialogTitle>
        <DialogDescription>
          Node {standbyNode}’s standby takes the production on air at the next term. Automatic failover did not, because nothing could confirm that {activeNode ?? 'the active node'} is down.
        </DialogDescription>
      </DialogHeader>
      <div role="alert" className="flex gap-3 rounded-md border border-destructive p-3 text-sm" data-testid="split-brain-warning">
        <TriangleAlertIcon className="size-5 shrink-0 text-destructive" aria-hidden="true" />
        <div className="grid gap-1">
          <strong className="text-destructive">Split-brain risk</strong>
          <span>
            If {activeNode ?? 'the old active'} is in fact alive and still reaches the devices, both nodes drive them until it hears the new term and mutes. Only do this when you know it is down — check its power and
            network, and watch the devices after.
          </span>
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="force-confirm">
          Type <span className="font-mono">{confirmation}</span> to confirm
        </Label>
        <Input id="force-confirm" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} data-testid="force-confirm" />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="force-reason">Why (kept in the audit and the alert)</Label>
        <Textarea id="force-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="force-reason" />
      </div>
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive" data-testid="force-error">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button variant="destructive" disabled={!ready || pending} onClick={() => onConfirm({ confirm: typed.trim(), reason: reason.trim() })} data-testid="force-go">
          {pending ? 'Forcing…' : 'Force on air'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function ForcePromoteDialog(props: ForcePromoteDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <Body {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
